import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { RefreshCw, Brain, TrendingDown, Building2, Users, FileCheck, Map, Loader2, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import AnalyticsBlockMapTab from "@/components/AnalyticsBlockMapTab";
import AnalyticsDropoutRiskTab from "@/components/AnalyticsDropoutRiskTab";
import AnalyticsInfrastructureTab from "@/components/AnalyticsInfrastructureTab";
import AnalyticsTeacherForecastTab from "@/components/AnalyticsTeacherForecastTab";
import AnalyticsDataCompletionTab from "@/components/AnalyticsDataCompletionTab";
import AnalyticsExecutiveInsightsTab from "@/components/AnalyticsExecutiveInsightsTab";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, ScatterChart, Scatter, ZAxis } from "recharts";
import { getBackendUrl } from "@/lib/backend";
import AiInsightsCard, { CollapsibleInsights } from "@/components/AiInsightsCard";
import "./AnalyticsDashboard.css";

const BACKEND_URL = getBackendUrl();
const ANALYTICS_TAB_IDS = ["map", "insights", "dropout", "infrastructure", "teacher", "completion"];
const DEFAULT_ANALYTICS_TAB = "map";

const normalizeAnalyticsTab = (value) => {
  if (!value) return DEFAULT_ANALYTICS_TAB;
  if (value === "executive") return "insights";
  return ANALYTICS_TAB_IDS.includes(value) ? value : DEFAULT_ANALYTICS_TAB;
};

const predictionTypeForTab = (tab) => (tab === "insights" ? "executive" : tab);
const API = `${BACKEND_URL}/api`;

const RAGBadge = ({ status }) => {
  const colors = {
    green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    High: "bg-red-100 text-red-700",
    Medium: "bg-amber-100 text-amber-700",
    Low: "bg-emerald-100 text-emerald-700"
  };
  return <Badge className={colors[status] || "bg-slate-100"}>{status}</Badge>;
};

// CollapsibleInsights + AiInsightsCard moved to a shared component so we can reuse
// the same 4 sections (Insights / Root Cause / Recommendations / Priority Actions)
// on District / Block / School drilldowns too.

const AnalyticsDashboard = () => {
  const { scope } = useScope();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  const activeTab = useMemo(() => {
    const fromAnalyticsParam = searchParams.get("analyticsTab");
    if (fromAnalyticsParam) return normalizeAnalyticsTab(fromAnalyticsParam);
    const legacyTab = searchParams.get("tab");
    if (legacyTab) return normalizeAnalyticsTab(legacyTab);
    return DEFAULT_ANALYTICS_TAB;
  }, [searchParams]);

  const setActiveTab = useCallback(
    (tab) => {
      const normalized = normalizeAnalyticsTab(tab);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          if (!normalized || normalized === DEFAULT_ANALYTICS_TAB) {
            next.delete("analyticsTab");
          } else {
            next.set("analyticsTab", normalized);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [mapData, setMapData] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState("classroom_health");
  
  const [dropoutData, setDropoutData] = useState(null);
  const [infraData, setInfraData] = useState(null);
  const [teacherData, setTeacherData] = useState(null);
  const [completionData, setCompletionData] = useState(null);
  const [executiveInsights, setExecutiveInsights] = useState(null);
  
  const [loadingPrediction, setLoadingPrediction] = useState(null);

  const fetchMapData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/map/block-metrics`, {
        params: {
          district_code: scope.districtCode || undefined,
          district_name: scope.districtName || undefined,
          block_code: scope.blockCode || undefined,
          block_name: scope.blockName || undefined,
          udise_code: scope.udiseCode || undefined,
          school_name: scope.schoolName || undefined,
        },
      });
      const blocks = response.data.blocks || [];

      // Enrich with real block_code for drilldown navigation.
      // analytics/map/block-metrics does not currently return block_code reliably.
      const districtCode = scope.districtCode || "2725"; // default Pune
      try {
        const scopeRes = await axios.get(`${API}/scope/districts/${districtCode}/blocks`, {
          headers: { "x-skip-scope": "true" },
        });
        const byName = new Map(
          (scopeRes.data || []).map((b) => [String(b.block_name || "").trim().toUpperCase(), b.block_code]),
        );

        const enriched = blocks.map((b) => {
          const key = String(b.block_name || "").trim().toUpperCase();
          return { ...b, block_code: b.block_code || byName.get(key) || null };
        });
        setMapData(enriched);
      } catch {
        setMapData(blocks);
      }
    } catch (error) {
      console.error("Error fetching map data:", error);
    }
  }, [
    scope.blockCode,
    scope.blockName,
    scope.districtCode,
    scope.districtName,
    scope.schoolName,
    scope.udiseCode,
    scope.version,
  ]);

  const fetchPrediction = useCallback(async (type) => {
    setLoadingPrediction(type);
    try {
      const endpoints = {
        dropout: "/analytics/predictions/dropout-risk",
        infrastructure: "/analytics/predictions/infrastructure-forecast",
        teacher: "/analytics/predictions/teacher-shortage",
        completion: "/analytics/predictions/data-completion",
        executive: "/analytics/insights/executive-summary"
      };
      
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}${endpoints[type]}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        params: {
          district_code: scope.districtCode || undefined,
          district_name: scope.districtName || undefined,
          block_code: scope.blockCode || undefined,
          block_name: scope.blockName || undefined,
          udise_code: scope.udiseCode || undefined,
          school_name: scope.schoolName || undefined,
        },
      });
      
      switch (type) {
        case "dropout": setDropoutData(response.data); break;
        case "infrastructure": setInfraData(response.data); break;
        case "teacher": setTeacherData(response.data); break;
        case "completion": setCompletionData(response.data); break;
        case "executive": setExecutiveInsights(response.data); break;
      }
      
      toast.success("AI analysis complete!");
    } catch (error) {
      const status = error?.response?.status;
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to generate prediction";

      // If session is invalid/expired, force re-login so protected endpoints work.
      if (status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        toast.error("Session expired. Please log in again.");
        navigate("/login");
      } else {
        toast.error(detail);
      }
      console.error("Failed to generate prediction:", { type, status, detail, error });
    } finally {
      setLoadingPrediction(null);
    }
  }, [
    navigate,
    scope.blockCode,
    scope.blockName,
    scope.districtCode,
    scope.districtName,
    scope.schoolName,
    scope.udiseCode,
    scope.version,
  ]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchMapData();
      setLoading(false);
    };
    loadData();
  }, [fetchMapData]);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    const hadDropout = Boolean(dropoutData);
    const hadInfra = Boolean(infraData);
    const hadTeacher = Boolean(teacherData);
    const hadCompletion = Boolean(completionData);
    const hadExecutive = Boolean(executiveInsights);

    setDropoutData(null);
    setInfraData(null);
    setTeacherData(null);
    setCompletionData(null);
    setExecutiveInsights(null);

    const activeGenerated = (
      (activeTab === "dropout" && hadDropout) ||
      (activeTab === "infrastructure" && hadInfra) ||
      (activeTab === "teacher" && hadTeacher) ||
      (activeTab === "completion" && hadCompletion) ||
      (activeTab === "insights" && hadExecutive)
    );

    if (activeTab !== "map" && activeGenerated) {
      fetchPrediction(predictionTypeForTab(activeTab));
    }
  }, [
    activeTab,
    fetchPrediction,
    scope.blockCode,
    scope.blockName,
    scope.districtCode,
    scope.districtName,
    scope.schoolName,
    scope.udiseCode,
    scope.version,
  ]);

  useEffect(() => {
    if (activeTab === "dropout" && !dropoutData && loadingPrediction !== "dropout") {
      fetchPrediction("dropout");
    }
  }, [activeTab, dropoutData, loadingPrediction, fetchPrediction]);

  useEffect(() => {
    if (activeTab === "infrastructure" && !infraData && loadingPrediction !== "infrastructure") {
      fetchPrediction("infrastructure");
    }
  }, [activeTab, infraData, loadingPrediction, fetchPrediction]);

  useEffect(() => {
    if (activeTab === "teacher" && !teacherData && loadingPrediction !== "teacher") {
      fetchPrediction("teacher");
    }
  }, [activeTab, teacherData, loadingPrediction, fetchPrediction]);

  useEffect(() => {
    if (activeTab === "completion" && !completionData && loadingPrediction !== "completion") {
      fetchPrediction("completion");
    }
  }, [activeTab, completionData, loadingPrediction, fetchPrediction]);

  useEffect(() => {
    if (activeTab === "insights" && !executiveInsights && loadingPrediction !== "executive") {
      fetchPrediction("executive");
    }
  }, [activeTab, executiveInsights, loadingPrediction, fetchPrediction]);

  useEffect(() => {
    const legacyTab = searchParams.get("tab");
    const analyticsTab = searchParams.get("analyticsTab");
    if (!legacyTab || analyticsTab) return;

    const normalized = normalizeAnalyticsTab(legacyTab);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        if (normalized === DEFAULT_ANALYTICS_TAB) {
          next.delete("analyticsTab");
        } else {
          next.set("analyticsTab", normalized);
        }
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-lg">
          <p className="font-medium">{data.block || data.block_name}</p>
          {Object.entries(data).filter(([k]) => k !== 'block' && k !== 'block_name').slice(0, 4).map(([key, value]) => (
            <p key={key} className="text-sm text-slate-300">{key}: {typeof value === 'number' ? value.toLocaleString() : value}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="loading-spinner" /></div>;
  }

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";

  return (
    <div className="analytics-dashboard space-y-6 animate-fade-in" data-testid="analytics-dashboard">
      <Tabs value={activeTab} onValueChange={setActiveTab} activationMode="automatic" className="space-y-6">
        <div className="analytics-tabs-wrap" aria-label="Advanced analytics sections">
          <TabsList className="analytics-tabs-list bg-transparent h-auto w-full justify-start">
            <TabsTrigger value="map" data-testid="analytics-tab-map"><Map className="w-4 h-4 mr-2" />Block Map</TabsTrigger>
            <TabsTrigger value="insights" data-testid="analytics-tab-insights"><Sparkles className="w-4 h-4 mr-2" />Executive Insights</TabsTrigger>
            <TabsTrigger value="dropout" data-testid="analytics-tab-dropout"><TrendingDown className="w-4 h-4 mr-2" />Dropout Risk</TabsTrigger>
            <TabsTrigger value="infrastructure" data-testid="analytics-tab-infrastructure"><Building2 className="w-4 h-4 mr-2" />Infrastructure</TabsTrigger>
            <TabsTrigger value="teacher" data-testid="analytics-tab-teacher"><Users className="w-4 h-4 mr-2" />Teacher Forecast</TabsTrigger>
            <TabsTrigger value="completion" data-testid="analytics-tab-completion"><FileCheck className="w-4 h-4 mr-2" />Data Completion</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="map" className="mt-0">
          <AnalyticsBlockMapTab
            mapData={mapData}
            selectedMetric={selectedMetric}
            onMetricChange={setSelectedMetric}
            onRefresh={fetchMapData}
            scopeLabel={scopeLabel}
            scope={scope}
          />
        </TabsContent>

        <TabsContent value="insights" className="mt-0">
          <AnalyticsExecutiveInsightsTab
            executiveData={executiveInsights}
            loading={loadingPrediction === "executive"}
            onRefresh={() => fetchPrediction("executive")}
            onGenerate={() => fetchPrediction("executive")}
            scopeLabel={scopeLabel}
          />
        </TabsContent>

        <TabsContent value="dropout" className="mt-0">
          <AnalyticsDropoutRiskTab
            dropoutData={dropoutData}
            loading={loadingPrediction === "dropout"}
            onRefresh={() => fetchPrediction("dropout")}
            onGenerate={() => fetchPrediction("dropout")}
            scopeLabel={scopeLabel}
          />
        </TabsContent>

        <TabsContent value="infrastructure" className="mt-0">
          <AnalyticsInfrastructureTab
            infraData={infraData}
            loading={loadingPrediction === "infrastructure"}
            onRefresh={() => fetchPrediction("infrastructure")}
            onGenerate={() => fetchPrediction("infrastructure")}
            scopeLabel={scopeLabel}
          />
        </TabsContent>

        <TabsContent value="teacher" className="mt-0">
          <AnalyticsTeacherForecastTab
            teacherData={teacherData}
            loading={loadingPrediction === "teacher"}
            onRefresh={() => fetchPrediction("teacher")}
            onGenerate={() => fetchPrediction("teacher")}
            scopeLabel={scopeLabel}
          />
        </TabsContent>

        <TabsContent value="completion" className="mt-0">
          <AnalyticsDataCompletionTab
            completionData={completionData}
            loading={loadingPrediction === "completion"}
            onRefresh={() => fetchPrediction("completion")}
            onGenerate={() => fetchPrediction("completion")}
            scopeLabel={scopeLabel}
          />
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;
